import React, { useEffect, useMemo, useState } from 'react'
import { Table, Empty, Modal } from 'antd'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { useLocation, useNavigate } from 'react-router-dom'
import useLandingPage from './useLandingPage'
import 'src/Pages/LibraryPages/librarypages.scss'
import 'src/Pages/dashboardHero.scss'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement)

const SunIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ffd166"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
  </svg>
)
const MoonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ffd166"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </svg>
)

const MentorDashboard = () => {
  const {
    groupData,
    loading,
    columns,
    chartLoading,
    statusData,
    completionData,
    page,
    pageSize,
    total,
    setPage,
    setPageSize,
  } = useLandingPage()
  const location = useLocation()
  const navigate = useNavigate()
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage)

  const handleOk = () => {
    setSuccessMessage(null)
    navigate(location.pathname, { replace: true, state: {} })
    window.location.reload()
  }

  const success = (message) => {
    Modal.success({ content: message, onOk: handleOk })
  }

  useEffect(() => {
    if (successMessage) {
      success(successMessage)
    }
  }, [successMessage])

  const { greeting, dateStr, isNight } = useMemo(() => {
    const now = new Date()
    const h = now.getHours()
    const g =
      h < 5
        ? 'Good night'
        : h < 12
        ? 'Good morning'
        : h < 17
        ? 'Good afternoon'
        : h < 21
        ? 'Good evening'
        : 'Good night'
    return {
      greeting: g,
      dateStr: now.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' }),
      isNight: h < 6 || h >= 19,
    }
  }, [])

  return (
    <div className="lpg dh">
      <div className="dh__hero">
        <span className="dh__rings" aria-hidden="true" />
        <div className="dh__date">{dateStr}</div>
        <h1 className="dh__greet">
          <span className="dh__ticon">{isNight ? <MoonIcon /> : <SunIcon />}</span>
          {greeting}, Mentor
        </h1>
        <p className="dh__sub">Welcome back. Here is an overview of your groups and projects.</p>
      </div>

      <div className="dh__eyebrow">Assigned groups</div>
      {loading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : (
        <div className="lpg__panel-box">
          <Table
            dataSource={groupData}
            columns={columns}
            bordered
            rowKey="groupId"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '30'],
              onChange: (p, s) => {
                setPage(p)
                setPageSize(s)
              },
            }}
          />
        </div>
      )}

      <div className="dh__eyebrow">Overview</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
        }}
      >
        <div className="lpg__panel-box">
          <h3 style={{ margin: '0 0 12px', fontWeight: 700, fontSize: '1rem', color: '#16233f' }}>
            Project Status
          </h3>
          {chartLoading ? (
            <div className="lpg__state">
              <div className="lpg__spin" />
              Loading...
            </div>
          ) : statusData && statusData.labels.length > 0 ? (
            <Bar
              data={statusData}
              options={{
                scales: {
                  y: {
                    ticks: {
                      callback: function (value) {
                        return value + '%'
                      },
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        let value = context.raw || 0
                        return value + '%'
                      },
                    },
                  },
                },
              }}
            />
          ) : (
            <Empty description="Project status data is not available" />
          )}
        </div>

        <div className="lpg__panel-box">
          <h3 style={{ margin: '0 0 12px', fontWeight: 700, fontSize: '1rem', color: '#16233f' }}>
            Completion Status
          </h3>
          <div style={{ width: '100%', maxWidth: '340px', height: '280px', margin: '0 auto' }}>
            {chartLoading ? (
              <div className="lpg__state">
                <div className="lpg__spin" />
                Loading...
              </div>
            ) : completionData && completionData.labels.length > 0 ? (
              <Doughnut data={completionData} options={{ maintainAspectRatio: false }} />
            ) : (
              <Empty description="Completion status data is not available" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MentorDashboard
